import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import {
  deletePitchWordDay,
  getPitchWordDay,
  getPitchWordPayload,
  isPitchWordApiSuccess,
  isPitchWordNotFound,
  isPlayersExist,
  listPitchWordPuzzles,
  needsForce,
  regeneratePitchWordDay,
  savePitchWordDay,
} from '../../services/pitchWordApi';
import {
  CATEGORIES,
  DAY_KEY_PATTERN,
  DIFFICULTIES,
  PAGE_SIZE,
  categoryLabel,
  difficultyLabel,
  dayToForm,
  emptyForm,
  extractWriteStats,
  formatIstDateTime,
  formatWriteStats,
  formToPayload,
  hintCosts,
  hintText,
  istDayKey,
  mergeDay,
  normalizePuzzle,
  playerCount,
  rewardAfterAllHints,
  sourceMeta,
  statusMeta,
  validatePitchWordForm,
  winFanEnergy,
  withDayKey,
  wordLength,
} from './pitchWordUtils';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  MenuItem,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

const PLAYER_STATS = [
  { key: 'in_progress', label: 'In progress' },
  { key: 'won', label: 'Won' },
  { key: 'lost', label: 'Lost' },
  { key: 'finished', label: 'Finished' },
];

function PlayerCounts({ day }) {
  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {PLAYER_STATS.map((stat) => (
        <Chip
          key={stat.key}
          size="small"
          variant="outlined"
          label={`${stat.label} ${playerCount(day, stat.key)}`}
        />
      ))}
    </Stack>
  );
}

function PitchWord() {
  const todayKey = istDayKey(0);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ limit: PAGE_SIZE, offset: 0, total: 0, hasMore: false });
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [todayState, setTodayState] = useState({ loading: true, day: null, missing: false, error: '' });
  const [dateInput, setDateInput] = useState(todayKey);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorDayKey, setEditorDayKey] = useState('');
  const [editorIsNew, setEditorIsNew] = useState(false);
  const [loadedDay, setLoadedDay] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [savingStatus, setSavingStatus] = useState('');
  const [regeneratingKey, setRegeneratingKey] = useState('');

  const [forcePrompt, setForcePrompt] = useState(null);
  const [deletePrompt, setDeletePrompt] = useState(null);
  const [deletingKey, setDeletingKey] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSessionExpired = useCallback(
    (res) => {
      const code = String(res?.statusCode || res?.status_code || '');
      if (code === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 1500);
        return true;
      }
      return false;
    },
    [showSnackbar]
  );

  const loadToday = useCallback(async () => {
    try {
      const res = await getPitchWordDay(todayKey);
      if (handleSessionExpired(res)) return;
      if (isPitchWordNotFound(res)) {
        setTodayState({ loading: false, day: null, missing: true, error: '' });
        return;
      }
      if (!isPitchWordApiSuccess(res)) {
        setTodayState({
          loading: false,
          day: null,
          missing: false,
          error: res?.message || 'Could not check today’s puzzle.',
        });
        return;
      }
      setTodayState({
        loading: false,
        day: normalizePuzzle(getPitchWordPayload(res), todayKey),
        missing: false,
        error: '',
      });
    } catch {
      setTodayState({ loading: false, day: null, missing: false, error: 'Could not check today’s puzzle.' });
    }
  }, [handleSessionExpired, todayKey]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listPitchWordPuzzles({ limit: PAGE_SIZE, offset });
      if (handleSessionExpired(res)) return;
      if (!isPitchWordApiSuccess(res)) {
        setError(res?.message || 'Failed to load Pitch Word.');
        return;
      }
      const data = getPitchWordPayload(res) || {};
      const puzzles = (Array.isArray(data.puzzles) ? data.puzzles : [])
        .map((row) => normalizePuzzle(row))
        .filter(Boolean);
      setRows(puzzles);
      setPagination({
        limit: data.pagination?.limit ?? PAGE_SIZE,
        offset: data.pagination?.offset ?? offset,
        total: data.pagination?.total ?? 0,
        hasMore: !!data.pagination?.hasMore,
      });
      const todayRow = puzzles.find((row) => row.day_key === todayKey);
      if (todayRow) {
        setTodayState({ loading: false, day: todayRow, missing: false, error: '' });
      } else {
        await loadToday();
      }
    } catch {
      setError('Failed to load Pitch Word.');
    } finally {
      setLoading(false);
    }
  }, [handleSessionExpired, loadToday, offset, todayKey]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const refreshAll = () => {
    loadList();
  };

  const applyDetail = (payload, dayKey, { status, source } = {}) => {
    const next = normalizePuzzle(withDayKey(payload, dayKey), dayKey);
    if (!next) return null;
    if (!next.status && status) next.status = status;
    if (!next.source && source) next.source = source;
    const sameDay = loadedDay && loadedDay.day_key === (next.day_key || dayKey) ? loadedDay : null;
    const merged = mergeDay(sameDay, next);
    setLoadedDay(merged);
    setForm(dayToForm(merged));
    setEditorDayKey(merged.day_key || dayKey);
    setEditorIsNew(false);
    setEditorOpen(true);
    setFormError('');
    return merged;
  };

  const openDay = async (dayKey) => {
    if (!DAY_KEY_PATTERN.test(dayKey)) {
      showSnackbar('Choose a date in YYYY-MM-DD.', 'error');
      return;
    }
    setEditorOpen(true);
    setEditorIsNew(false);
    setEditorDayKey(dayKey);
    setEditorLoading(true);
    setFormError('');
    setLoadedDay(null);
    try {
      const res = await getPitchWordDay(dayKey);
      if (handleSessionExpired(res)) return;
      if (isPitchWordNotFound(res)) {
        setEditorIsNew(true);
        setLoadedDay(null);
        setForm(emptyForm());
        setFormError('');
        return;
      }
      if (!isPitchWordApiSuccess(res)) {
        setFormError(res?.message || 'Failed to load this day.');
        return;
      }
      const day = normalizePuzzle(getPitchWordPayload(res), dayKey);
      setLoadedDay(day);
      setForm(dayToForm(day));
    } catch {
      setFormError('Failed to load this day.');
    } finally {
      setEditorLoading(false);
    }
  };

  const runSave = async (status, force) => {
    const validation = validatePitchWordForm(form);
    if (validation) {
      setFormError(validation);
      return;
    }
    if (!DAY_KEY_PATTERN.test(editorDayKey)) {
      setFormError('Choose a date in YYYY-MM-DD.');
      return;
    }

    setSavingStatus(status);
    setFormError('');
    try {
      const res = await savePitchWordDay(
        editorDayKey,
        formToPayload(form, loadedDay, { status, force })
      );
      if (handleSessionExpired(res)) return;
      if (!isPitchWordApiSuccess(res)) {
        if (!force && needsForce(res)) {
          setForcePrompt({
            action: 'save',
            status,
            dayKey: editorDayKey,
            message: res?.message || 'Players already have a word for this day.',
          });
          return;
        }
        setFormError(res?.message || 'Save failed. Nothing was stored.');
        return;
      }
      const payload = getPitchWordPayload(res);
      const applied = applyDetail(payload, editorDayKey, { status, source: 'admin' });
      if (!applied) openDay(editorDayKey);
      const summary = formatWriteStats(extractWriteStats(payload));
      const lead = status === 'published'
        ? 'Published. Players can open this day.'
        : 'Draft saved. Players who have not started this day will not see it.';
      showSnackbar(summary ? `${lead} ${summary}` : lead);
      setForcePrompt(null);
      loadList();
    } catch {
      setFormError('Save failed. Nothing was stored.');
    } finally {
      setSavingStatus('');
    }
  };

  const runRegenerate = async (dayKey, force) => {
    if (!DAY_KEY_PATTERN.test(dayKey)) {
      showSnackbar('Choose a date in YYYY-MM-DD.', 'error');
      return;
    }
    setRegeneratingKey(dayKey);
    try {
      const res = await regeneratePitchWordDay(dayKey, { force });
      if (handleSessionExpired(res)) return;
      if (!isPitchWordApiSuccess(res)) {
        if (!force && needsForce(res)) {
          setForcePrompt({
            action: 'regenerate',
            dayKey,
            message: res?.message || 'Players have already opened this published day.',
          });
          return;
        }
        showSnackbar(res?.message || 'Regenerate failed. The current puzzle was left as it is.', 'error');
        return;
      }
      const payload = getPitchWordPayload(res);
      if (!applyDetail(payload, dayKey, { status: 'published', source: 'ai' })) {
        openDay(dayKey);
      }
      const summary = formatWriteStats(extractWriteStats(payload));
      const lead = res?.message || 'Pitch Word day regenerated.';
      const live = 'Players can open it now.';
      showSnackbar([lead, live, summary].filter(Boolean).join(' '));
      setForcePrompt(null);
      loadList();
    } catch {
      showSnackbar('Regenerate failed. The current puzzle was left as it is.', 'error');
    } finally {
      setRegeneratingKey('');
    }
  };

  const runDelete = async (dayKey, force) => {
    if (!DAY_KEY_PATTERN.test(dayKey)) {
      showSnackbar('Choose a date in YYYY-MM-DD.', 'error');
      return;
    }
    setDeletingKey(dayKey);
    try {
      const res = await deletePitchWordDay(dayKey, { force });
      if (handleSessionExpired(res)) return;
      if (!isPitchWordApiSuccess(res)) {
        if (!force && isPlayersExist(res)) {
          setDeletePrompt({ dayKey, needsForce: true });
          return;
        }
        showSnackbar(res?.message || 'Could not delete this day.', 'error');
        return;
      }
      setDeletePrompt(null);
      if (editorDayKey === dayKey) {
        setEditorOpen(false);
        setLoadedDay(null);
      }
      showSnackbar(
        `${dayKey} deleted. The hourly job will not create it again. Save or regenerate this date to add a puzzle.`
      );
      loadList();
    } catch {
      showSnackbar('Could not delete this day.', 'error');
    } finally {
      setDeletingKey('');
    }
  };

  const updateExtraHint = (index, value) => {
    setForm((current) => {
      const extraHints = [...current.extra_hints];
      extraHints[index] = value;
      return { ...current, extra_hints: extraHints };
    });
  };

  const busy = !!savingStatus || !!regeneratingKey || !!deletingKey;
  const costs = hintCosts(loadedDay);
  const from = pagination.total === 0 ? 0 : pagination.offset + 1;
  const to = pagination.offset + rows.length;
  const tomorrowKey = istDayKey(1);
  const tomorrowRow = offset === 0 ? rows.find((row) => (row.day_key || row.date) === tomorrowKey) : null;

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 1 }}>
            <Button component={Link} to="/admin/arcade" startIcon={<ArrowBackIcon />} size="small">
              Arcade
            </Button>
            <Typography variant="h5" sx={{ flex: 1 }}>
              Pitch Word
            </Typography>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={refreshAll} disabled={loading} aria-label="Refresh">
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
            One puzzle per IST date. An hourly job publishes today and tomorrow. Opening this list creates today
            when that row is missing, and leaves a deleted date empty. Players only see a published puzzle, and they
            do not see the answer until they finish. Edit copy, regenerate a bad day, hold a day as a draft, or delete
            a date. A draft stays off the app, and the hourly job will not replace that hold or refill a deleted date.
          </Typography>

          {todayState.loading ? null : todayState.error ? (
            <Alert severity="error" sx={{ mb: 2 }}>{todayState.error}</Alert>
          ) : todayState.missing ? (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={<Button color="inherit" size="small" onClick={() => openDay(todayKey)}>Open</Button>}
            >
              Today ({todayKey}) was deleted. The hourly job will not create it again, and players have no puzzle for
              this date. Regenerate or save a puzzle to bring it back.
            </Alert>
          ) : todayState.day?.status !== 'published' ? (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={<Button color="inherit" size="small" onClick={() => openDay(todayKey)}>Open</Button>}
            >
              Today ({todayKey}) is held as a draft{todayState.day?.answer ? `: ${todayState.day.answer}` : ''}.
              Players who have not started will not see it. The hourly job will not replace this hold.
            </Alert>
          ) : todayState.day?.source === 'fallback' ? (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={<Button color="inherit" size="small" onClick={() => openDay(todayKey)}>Open</Button>}
            >
              Today ({todayKey}) is published from the catalog. Answer {todayState.day?.answer || '—'}.
              {todayState.day?.generation_error ? ` ${todayState.day.generation_error}` : ''}
              {' '}The hourly job tries the model again until a player opens this day. Regenerate to replace it now.
            </Alert>
          ) : (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              action={<Button color="inherit" size="small" onClick={() => openDay(todayKey)}>Open</Button>}
            >
              Today ({todayKey}) is published. Answer {todayState.day?.answer || '—'}. Players still do not see the
              word until they finish.
            </Alert>
          )}

          {!loading && offset === 0 && tomorrowRow?.status === 'draft' && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={<Button color="inherit" size="small" onClick={() => openDay(tomorrowKey)}>Open</Button>}
            >
              Tomorrow ({tomorrowKey}) is held as a draft{tomorrowRow.answer ? `: ${tomorrowRow.answer}` : ''}.
              The hourly job will not replace this hold.
            </Alert>
          )}
          {!loading && offset === 0 && tomorrowRow?.status === 'published' && tomorrowRow?.source === 'fallback' && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={<Button color="inherit" size="small" onClick={() => openDay(tomorrowKey)}>Open</Button>}
            >
              Tomorrow ({tomorrowKey}) is published from the catalog. Answer {tomorrowRow.answer || '—'}.
              {tomorrowRow.generation_error ? ` ${tomorrowRow.generation_error}` : ''}
              {' '}The hourly job tries the model again until a player opens it.
            </Alert>
          )}
          {!loading && !error && offset === 0 && !tomorrowRow && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Tomorrow ({tomorrowKey}) is not in this list. The hourly job creates it about a day ahead, unless this
              date was deleted. A deleted date stays empty until you regenerate or save it.
            </Alert>
          )}

          {regeneratingKey && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Generating {regeneratingKey}. This usually takes 30–60 seconds, and the new puzzle is published when it succeeds.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <TextField
                label="Date (IST)"
                type="date"
                size="small"
                value={dateInput}
                onChange={(event) => setDateInput(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="outlined" onClick={() => openDay(dateInput)} disabled={busy}>
                Open
              </Button>
              <Button
                variant="contained"
                startIcon={regeneratingKey === dateInput ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />}
                onClick={() => runRegenerate(dateInput, false)}
                disabled={busy}
              >
                Regenerate
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Regenerate replaces that day and publishes it, including a draft. If players have already opened it, you
              will be asked to confirm.
            </Typography>
          </Paper>

          {editorOpen && (
            <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  {editorDayKey || 'Puzzle'}
                </Typography>
                {loadedDay?.status && (
                  <Chip size="small" label={statusMeta(loadedDay.status).label} color={statusMeta(loadedDay.status).color} />
                )}
                {loadedDay?.source && (
                  <Chip size="small" label={sourceMeta(loadedDay.source).label} color={sourceMeta(loadedDay.source).color} />
                )}
                <Button size="small" onClick={() => setEditorOpen(false)} disabled={busy}>
                  Close
                </Button>
              </Box>

              {editorLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">Loading puzzle…</Typography>
                </Box>
              ) : (
                <>
                  {loadedDay?.source === 'fallback' && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      This day is published from the catalog because the model could not write a valid puzzle.
                      {loadedDay.generation_error ? ` ${loadedDay.generation_error}` : ''}
                      {' '}The hourly job tries again until a player opens it. Regenerate replaces the word and publishes it now.
                    </Alert>
                  )}
                  {editorIsNew && !loadedDay && !formError && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No puzzle is saved for this date. Regenerate writes one and publishes it, or type a puzzle and save it.
                    </Alert>
                  )}
                  {formError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>
                      {formError}
                    </Alert>
                  )}

                  {editorIsNew && (
                    <TextField
                      label="Date (IST)"
                      type="date"
                      size="small"
                      value={editorDayKey}
                      onChange={(event) => {
                        const nextKey = event.target.value;
                        setEditorDayKey(nextKey);
                        if (DAY_KEY_PATTERN.test(nextKey)) openDay(nextKey);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ mb: 1 }}
                    />
                  )}

                  <TextField
                    label="Answer"
                    value={form.answer}
                    onChange={(event) => setForm((current) => ({
                      ...current,
                      answer: event.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 6),
                    }))}
                    helperText="4 to 6 letters"
                    inputProps={{ maxLength: 6, style: { letterSpacing: '0.2em', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' } }}
                    fullWidth
                    margin="dense"
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      select
                      label="Category"
                      value={form.category}
                      onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}
                      helperText="Blank keeps the current value, or looks the new word up in the catalog."
                      fullWidth
                      margin="dense"
                    >
                      <MenuItem value="">Not set</MenuItem>
                      {CATEGORIES.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Difficulty"
                      value={form.difficulty}
                      onChange={(event) => setForm((current) => ({ ...current, difficulty: event.target.value }))}
                      helperText="Blank keeps the current value when the answer stays the same."
                      fullWidth
                      margin="dense"
                    >
                      <MenuItem value="">Not set</MenuItem>
                      {DIFFICULTIES.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                  <TextField
                    label="Title"
                    value={form.title}
                    onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                    fullWidth
                    margin="dense"
                  />
                  <TextField
                    label="Main hint"
                    value={form.main_hint}
                    onChange={(event) => setForm((current) => ({ ...current, main_hint: event.target.value }))}
                    fullWidth
                    margin="dense"
                    multiline
                    minRows={2}
                  />
                  {[0, 1, 2].map((index) => (
                    <TextField
                      key={index}
                      label={`Extra hint ${index + 1}`}
                      value={form.extra_hints[index] || ''}
                      onChange={(event) => updateExtraHint(index, event.target.value)}
                      fullWidth
                      margin="dense"
                      multiline
                      minRows={2}
                      helperText={`Takes ${costs[index] ?? '—'} off the win reward`}
                    />
                  ))}

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Max win {winFanEnergy(loadedDay)} FE with no extra hints. Revealing all three pays{' '}
                    {rewardAfterAllHints(loadedDay)} FE. A win never pays below 0. Hints do not debit the player&apos;s balance.
                  </Typography>

                  {loadedDay && (
                    <Box sx={{ mt: 1.5 }}>
                      <PlayerCounts day={loadedDay} />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        {loadedDay.max_guesses ?? 6} guesses
                        {loadedDay.model ? ` · ${loadedDay.model}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Updated {formatIstDateTime(loadedDay.updated_at)}
                        {loadedDay.created_at ? ` · Created ${formatIstDateTime(loadedDay.created_at)}` : ''}
                      </Typography>
                    </Box>
                  )}

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1.5 }}>
                    Save as draft holds the day off the app, and the hourly job will not overwrite that hold. Publish
                    is what players can open. Saving a draft does not push a new word to players who already started.
                    Changing the answer on a day players have already opened asks you to confirm, including when you
                    publish a draft whose word differs from the one they started. In-progress players take the new
                    puzzle. Finished players keep the word they played.
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button
                      variant="outlined"
                      onClick={() => runSave('draft', false)}
                      disabled={busy}
                    >
                      {savingStatus === 'draft' ? <CircularProgress size={20} /> : 'Save as draft'}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => runSave('published', false)}
                      disabled={busy}
                    >
                      {savingStatus === 'published' ? <CircularProgress size={20} color="inherit" /> : 'Publish'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={regeneratingKey === editorDayKey ? <CircularProgress size={16} /> : <AutorenewIcon />}
                      onClick={() => runRegenerate(editorDayKey, false)}
                      disabled={busy || !DAY_KEY_PATTERN.test(editorDayKey)}
                    >
                      Regenerate
                    </Button>
                    {loadedDay && (
                      <Button
                        color="error"
                        onClick={() => setDeletePrompt({ dayKey: editorDayKey, needsForce: false })}
                        disabled={busy}
                      >
                        Delete day
                      </Button>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Regenerate writes a new puzzle and publishes it. The word has not been used before, its length
                    differs from yesterday, and its category differs from the last two days. If generation fails, this
                    puzzle stays as it is. This usually takes 30–60 seconds.
                  </Typography>
                </>
              )}
            </Paper>
          )}

          <Typography variant="h6" sx={{ mb: 1 }}>
            Puzzles
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Title and hints</TableCell>
                  <TableCell>Answer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Players</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">Loading puzzles…</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No puzzles saved. Opening this list creates today unless that date was deleted.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => {
                  const dayKey = row.day_key || row.date;
                  const status = statusMeta(row.status);
                  const source = sourceMeta(row.source);
                  const rowBusy = regeneratingKey === dayKey;
                  return (
                    <TableRow
                      key={dayKey}
                      hover
                      selected={editorOpen && editorDayKey === dayKey}
                      sx={dayKey === todayKey ? { bgcolor: 'rgba(11, 83, 69, 0.06)' } : undefined}
                    >
                      <TableCell>{dayKey}</TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2">{row.title || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'normal' }}>
                          {hintText(row.main_hint) || 'No main hint'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'normal' }}>
                          {(Array.isArray(row.extra_hints) ? row.extra_hints : [])
                            .map((hint, index) => `${index + 1}. ${hintText(hint) || '—'} (−${hintCosts(row)[index] ?? '—'})`)
                            .join('  ')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'normal' }}>
                          Win up to {winFanEnergy(row)} FE. All hints revealed pays {rewardAfterAllHints(row)} FE.
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 700 }}>
                          {row.answer}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {wordLength(row) ? `${wordLength(row)} letters` : 'Length not set'}
                          {' · '}
                          {categoryLabel(row.category)}
                          {' · '}
                          {difficultyLabel(row.difficulty)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={status.label} color={status.color} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={source.label} color={source.color} variant="outlined" />
                        {row.model ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {row.model}
                          </Typography>
                        ) : null}
                        {row.source === 'fallback' && row.generation_error ? (
                          <Typography variant="caption" color="warning.main" display="block" sx={{ whiteSpace: 'normal', maxWidth: 220 }}>
                            {row.generation_error}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">In progress {playerCount(row, 'in_progress')}</Typography>
                        <Typography variant="caption" display="block">Won {playerCount(row, 'won')}</Typography>
                        <Typography variant="caption" display="block">Lost {playerCount(row, 'lost')}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Finished {playerCount(row, 'finished')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" startIcon={<EditIcon />} onClick={() => openDay(dayKey)} disabled={busy}>
                          Open
                        </Button>
                        <Button
                          size="small"
                          startIcon={rowBusy ? <CircularProgress size={14} /> : <AutorenewIcon />}
                          onClick={() => runRegenerate(dayKey, false)}
                          disabled={busy}
                        >
                          Regenerate
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          aria-label={`Delete ${dayKey}`}
                          onClick={() => setDeletePrompt({ dayKey, needsForce: false })}
                          disabled={busy}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {pagination.total === 0 ? '0 puzzles' : `${from}–${to} of ${pagination.total}`}
            </Typography>
            <Button size="small" disabled={loading || offset <= 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              Previous
            </Button>
            <Button size="small" disabled={loading || !pagination.hasMore} onClick={() => setOffset(offset + PAGE_SIZE)}>
              Next
            </Button>
          </Box>
        </div>
      </div>

      <Dialog open={!!deletePrompt} onClose={() => !deletingKey && setDeletePrompt(null)}>
        <DialogTitle>{deletePrompt?.needsForce ? 'Players already opened this day' : 'Delete this day?'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {deletePrompt?.needsForce
              ? `Players have already opened ${deletePrompt.dayKey}. Deleting removes the puzzle anyway. Attempt rows stay, and Fan Energy already awarded is not reversed. The date is not playable until you save or regenerate it.`
              : `Remove the puzzle for ${deletePrompt?.dayKey}? The hourly job will not create it again. Players have no puzzle for that date, including someone who already started. If players have opened it, you will be asked to confirm.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePrompt(null)} disabled={!!deletingKey}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!!deletingKey}
            onClick={() => deletePrompt && runDelete(deletePrompt.dayKey, !!deletePrompt.needsForce)}
          >
            {deletingKey ? 'Deleting…' : deletePrompt?.needsForce ? 'Delete anyway' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!forcePrompt} onClose={() => !busy && setForcePrompt(null)}>
        <DialogTitle>Players already have this day</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {forcePrompt?.message}
          </Typography>
          <Typography variant="body2">
            {forcePrompt?.action === 'regenerate'
              ? `Regenerating ${forcePrompt?.dayKey} replaces the word and publishes it now. Players still in progress take the new puzzle. Finished players keep the word they played.`
              : forcePrompt?.status === 'draft'
                ? `Saving ${forcePrompt?.dayKey} as a draft does not change the word players already have.`
                : `Publishing ${forcePrompt?.dayKey} replaces the word for players still in progress. Finished players keep the word they played.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForcePrompt(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={busy}
            onClick={() => {
              if (!forcePrompt) return;
              if (forcePrompt.action === 'regenerate') runRegenerate(forcePrompt.dayKey, true);
              else runSave(forcePrompt.status, true);
            }}
          >
            {busy ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Component>
  );
}

export default PitchWord;
